/**
 * RBAC — one app, three MAJOR personas, each with its own role set.
 * Tenancy invariant: orgId/personaId ALWAYS comes from the JWT, never the request body.
 */
/** The three top-level personas the whole product is organised around. */
export const PERSONAS = ["admin", "user", "candidate"];
/**
 * ADMIN persona = TalentHop's own people. Exactly three roles, and deliberately NOT a rank
 * ladder: the previous owner/platform_admin/finance/support ladder could not express
 * "sees a lot, changes nothing", so every read gate and every write gate collapsed onto the
 * same rung and finance/support became indistinguishable. Reach is now declared per surface.
 * This list is mirrored by the users_admin_role_check constraint (migration 0033).
 */
export const ADMIN_ROLES = ["owner", "admin", "analyst"];
/** USER persona = a client/hiring org. One super-admin per org provisions the rest. */
export const ORG_ROLES = [
    "super_admin",
    "recruiter",
    "hiring_manager",
    "interviewer",
    "reviewer",
    "billing_admin",
    "external_client", // delegated, scoped, revocable (agency → client chain)
];
/** Rank ladder for "at least" checks within an org. */
const ORG_RANK = {
    super_admin: 100,
    billing_admin: 60,
    hiring_manager: 55,
    recruiter: 50,
    interviewer: 30,
    reviewer: 20,
    external_client: 10,
};
export function orgRoleAtLeast(role, min) {
    return ORG_RANK[role] >= ORG_RANK[min];
}
/**
 * 0043 email identity: which membership is THE session org. One live membership per user is
 * now a database invariant, but session resolution must still be deterministic while older
 * environments (or a mid-suspension race) hold several: highest rank wins, ties break on
 * orgId so two calls can never disagree. An unknown role ranks below everything — same
 * deny-the-unresolvable stance as adminCan. Replaces the old `order by role limit 1`, which
 * picked ALPHABETICALLY (billing_admin over super_admin).
 */
export function pickSessionMembership(memberships) {
    return [...memberships].sort((a, b) => {
        const byRank = (ORG_RANK[b.role] ?? 0) - (ORG_RANK[a.role] ?? 0);
        return byRank !== 0 ? byRank : a.orgId.localeCompare(b.orgId);
    })[0];
}
/**
 * Wave 3: a "limited" org role is a scoped external reviewer (`external_client`) — blocked from
 * billing, team/members, pricing, and from creating/closing requisitions or hiring decisions.
 * The api uses this to gate those routes (403).
 */
export function isLimitedRole(role) {
    return role === "external_client";
}
/**
 * The Admin console's five gateable surfaces. Every admin route belongs to exactly one, so a
 * role's reach is a set of surfaces plus a single write bit — not a number to compare.
 */
export const ADMIN_SURFACES = ["client_orgs", "sourcing", "money", "platform", "security"];
/**
 * The whole admin authorization model, in one table (owner's decision, 2026-07-28):
 *   owner   — everything, read and write.
 *   admin   — client orgs + the sourcing desk, read and write. No money, no platform config,
 *             no security/admin-account screens.
 *   analyst — the sourcing desk only, and never a write.
 */
export const ADMIN_CAPS = {
    owner: { surfaces: [...ADMIN_SURFACES], write: true },
    admin: { surfaces: ["client_orgs", "sourcing"], write: true },
    analyst: { surfaces: ["sourcing"], write: false },
};
export function adminCan(role, surface, write) {
    const caps = ADMIN_CAPS[role];
    // A role we have never heard of (a stale token, a hand-edited DB row, a value from a future
    // migration) resolves to undefined here. Deny it — the ladder this replaced silently answered
    // `undefined >= n` = false for reads but was easy to misread as "unknown means lowest rank".
    if (!caps)
        return false;
    if (!caps.surfaces.includes(surface))
        return false;
    return write ? caps.write : true;
}
/**
 * Individual ACTIONS that stay owner-only even where a surface gate would otherwise be enough.
 * Every entry lives in a surface only Owner can reach (money/platform/security), so this list
 * and ADMIN_CAPS agree by construction — it is kept because these are the irreversible ones and
 * they must not quietly widen if a surface is ever handed to Admin.
 */
export const OWNER_ONLY = [
    "impersonate_org", // "act as company" — policy-gated + audited
    "manage_admins", // provision / reset password / enable+reset MFA
    "rotate_secrets",
    "configure_payments",
    "toggle_feature_flags",
    "erase_candidate_data", // resolving a DSR by crypto-shredding a persona (decision #22a)
    "mint_credit_units", // creating or draining credit units out of thin air (decision #22b)
];
//# sourceMappingURL=roles.js.map